import { Container, InputAdornment, TextField } from "@material-ui/core";
import { Search } from "@material-ui/icons";
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";

import {
  BlockedModal,
  Category,
  Client,
  Product,
  ProductModal,
} from "../../components";

import { getCategories, getProducts } from "../../services";
import { useStore } from "../../store";

import {
  CategoryArray,
  ProductArray,
  Product as ProductInterface,
  Sector as SectorType,
} from "../../types";

import useStyles from "./styles";

import { useGetEntity, useGetSectorsActive } from "../../api/orders/hooks";
import { Sector } from "../../components/Sector";
import { getFirebaseRealTimeDatabase, getRealTimeProducts } from "../../services/firebaseRealTimeDatabase";
import {
  getDefaultSectorId,
  getScopedSectors,
  matchesSectorScope,
  shouldShowSectorOthers,
} from "../../utils/sectorScope";

export const MarketPurchase: React.FC = () => {
  const { userManager, userTotem } = useStore();

  const [openProductModal, setOpenProductModal] = useState<boolean>(false);
  const [openBlockedModal, setOpenBlockedModal] = useState<boolean>(false);

  const [onShowSectors, setOnShowSectors] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("0");
  const [categories, setCategories] = useState<any>([]);
  const [message, setMessage] = useState<string>("");
  const [selectedProduct, setSelectedProduct] = useState<ProductInterface | null>(null);
  const [sectorsList, setSectorsList] = useState<any[]>([]);
  const [sectorId, setSectorId] = useState("");
  const [quantitySectors, setQuantitySectors] = useState(0);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [realTimeProducts, setRealTimeProducts] = useState<ProductInterface[]>([]);

  const Styles = useStyles({ onShowSectors, quantitySectors });

  const handleClickInProduct = (product: ProductInterface) => {
    setSelectedProduct(product);
    setOpenProductModal(true);
  };

  const closeModal = () => {
    setOpenProductModal(false);
    setSelectedProduct(null);
  };

  const isBlockedProduct = (price: number): boolean =>
    price / 100 > userTotem.wallet.periodLimit - userTotem.wallet.spent &&
    !!userTotem.id;

  const { data: entity } = useGetEntity(userManager.entity, true);
  const { data: sectors } = useGetSectorsActive(userManager.entity, true);
  const scopedSectors = useMemo(
    () => getScopedSectors(sectors, userManager.segmentId),
    [sectors, userManager.segmentId]
  );
  const hasFixedSegment = !!userManager.segmentId;
  const { data: categoryData, isLoading: loadingCategory } = useQuery<
    CategoryArray,
    Error
  >("categoriesData", () => getCategories(userManager.entity));
  const {
    data: productsData,
    isLoading: loadingProducts,
    refetch: getProductsQuery,
  } = useQuery<ProductArray, Error>(
    ["productsData", userManager.entity, userTotem.id],
    () => getProducts(userManager.entity, userTotem.id)
  );

  useEffect(() => {
    const unsubscribe = getRealTimeProducts(userManager.entity, (products: ProductInterface[]) => {
      setRealTimeProducts(products);
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [userManager.entity]);

  const getProductWithRealTimeQuantity = (product: ProductInterface) => {
    const realTimeProduct = realTimeProducts.find((p) => p.id === product.id);
    if (realTimeProduct) {
      return { ...product, quantity: realTimeProduct.quantity };
    }
    return product;
  };

  const filterProducts = () => {
    const products = productsData?.filter((product) => !product.isvariablePrice) ?? [];

    const filterByCategory = (product: any) => product?.productCategory?.id === selectedCategory;
    const filterBySector = (product: any) =>
      matchesSectorScope(product.segment, sectorId, userManager.segmentId);
    const filterByHighlight = (product: any) => product.isHighlighted;
    const filterByNoSegment = (product: any) => !product.segment;
    const filterBySearch = (product: any) =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase());

    let filtered: ProductInterface[] = [];

    // If there's a search term, search across ALL products
    if (searchTerm) {
      filtered = products.filter(
        (product) =>
          filterBySearch(product) &&
          (!onShowSectors || filterBySector(product))
      );
    } else {
      // Otherwise, apply sector/category filters
      // eslint-disable-next-line no-lonely-if
      if (onShowSectors) {
        if (selectedCategory !== "0") {
          if (isSectorSelectedOthers(sectorId)) {
            filtered = products.filter(
              (product) => filterByNoSegment(product) && filterByCategory(product)
            );
          } else {
            filtered = products.filter(
              (product) => filterByCategory(product) && filterBySector(product)
            );
          }
        } else if (isSectorSelectedOthers(sectorId)) {
          filtered = products.filter(
            (product) => filterByHighlight(product) && filterByNoSegment(product)
          );
        } else {
          filtered = products.filter(
            (product) => filterByHighlight(product) && filterBySector(product)
          );
        }
      } else {
        filtered =
          selectedCategory !== "0"
            ? products.filter(filterByCategory)
            : products.filter(filterByHighlight);
      }
    }

    return filtered.map(getProductWithRealTimeQuantity);
  };

  const isSectorSelectedOthers = (id: string) =>
    shouldShowSectorOthers(userManager.segmentId) && id === "others";

  const filterProductsSectors = (id?: string) => {
    if (id && productsData && categoryData) {
      setSelectedCategory("0");
      let filteredProducts: any[] = [];
      if (isSectorSelectedOthers(id)) {
        filteredProducts = productsData.filter((prod: any) => !prod.segment);
      } else {
        filteredProducts = productsData.filter(
          (prod: any) =>
            matchesSectorScope(prod.segment, id, userManager.segmentId) &&
            !prod.isvariablePrice
        );
      }

      const uniqueCategoryIds = new Set(
        filteredProducts.map((p: any) => p.productCategory.id)
      );
      const uniqueCategories = categoryData.filter((cat: any) => uniqueCategoryIds.has(cat.id));
      setCategories(uniqueCategories);
    }
    return [];
  };

  const handleClickProduct = (product: ProductInterface) => {
    if (!product.isLocked) {
      if (isBlockedProduct(product.price)) {
        setOpenBlockedModal(true);
      } else if (product.quantity === 0) {
        setMessage("Esse item não tem mais estoque");
        setOpenBlockedModal(true);
      } else {
        handleClickInProduct(product);
      }
    }
  };

  const close = () => {
    setOpenBlockedModal(false);
    setMessage("");
  };

  const refresh = () => {
    if (userTotem.id) {
      getProductsQuery();
    }
  };

  const onSectorsList = () => {
    const secList = scopedSectors.map((s: SectorType, i: number) => ({
      ...s,
      selected: i === 0,
    }));
    secList.sort((a: SectorType, b: SectorType) => a.name.localeCompare(b.name));
    if (shouldShowSectorOthers(userManager.segmentId)) {
      secList.push({
        id: "others",
        name: "Outros",
        isActive: true,
        selected: false,
        products: [],
      });
    }
    setSectorsList(secList);
  };

  useEffect(() => {
    if (scopedSectors.length > 0 && onShowSectors) {
      onSectorsList();
      setQuantitySectors(scopedSectors.length);
      const defaultSectorId = getDefaultSectorId(
        scopedSectors,
        userManager.segmentId
      );
      setSectorId(defaultSectorId);
      filterProductsSectors(defaultSectorId);
    }
  }, [scopedSectors, categoryData, productsData, onShowSectors, userManager.segmentId]);

  useEffect(() => {
    if (hasFixedSegment && userManager.segmentId && categoryData && productsData) {
      setOnShowSectors(true);
      setSectorId(userManager.segmentId);
      filterProductsSectors(userManager.segmentId);
    }
  }, [hasFixedSegment, userManager.segmentId, categoryData, productsData]);

  useEffect(() => {
    if (categoryData && !onShowSectors) {
      setCategories(categoryData);
    }
  }, [categoryData, onShowSectors]);

  useEffect(() => {
    if (
      (entity?.isSectorsOnThePurchaseTotem || hasFixedSegment) &&
      scopedSectors.length > 0
    ) {
      setOnShowSectors(true);
    }
  }, [entity, scopedSectors, hasFixedSegment]);

  useEffect(() => {
    getFirebaseRealTimeDatabase(userManager.entity, refresh);
  }, []);

  return (
    <Container className={Styles.container} maxWidth={false}>
      {onShowSectors && (
        <>
          <div className={Styles.sectors}>
            <div className={Styles.sectorsContainer}>
              {sectorsList?.map((sec) => (
                <Sector
                  {...sec}
                  key={sec.id}
                  name={sec.name}
                  onClick={(id) => filterProductsSectors(id) && setSectorId(id)}
                  selected={sec.id === sectorId}
                />
              ))}
            </div>
            <div className={Styles.divider} />
          </div>
        </>
      )}

      <div className={Styles.searchContainer}>
        <TextField
          className={Styles.searchField}
          placeholder="Pesquisar produtos..."
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {!loadingCategory && categories.length > 0 && (
        <div className={Styles.categories}>
          <Category
            id="0"
            picture=""
            name="Destaques"
            onClick={(id) => setSelectedCategory(id)}
            selected={selectedCategory === "0"}
          />
          {categories
            .sort((a: any, b: any) => a.name.localeCompare(b.name))
            .map((category: any) => (
              <Category
                key={category.id}
                {...category}
                onClick={(id) => setSelectedCategory(id)}
                selected={category.id === selectedCategory}
              />
            ))}
        </div>
      )}

      <Client onShowSectors={onShowSectors} isMarket />

      <Container className={Styles.content} maxWidth={false}>
        {!loadingProducts && (
          <div className={Styles.products}>
            {filterProducts().map((product: any) => (
              <Product
                key={product.id}
                {...product}
                isLocked={
                  isBlockedProduct(product.price) ||
                  product.isLocked ||
                  product.quantity === 0
                }
                onClick={() => handleClickProduct(product)}
              />
            ))}
          </div>
        )}
      </Container>

      <BlockedModal message={message} open={openBlockedModal} onClose={close} />

      {selectedProduct && (
        <ProductModal
          open={openProductModal}
          onClose={closeModal}
          product={getProductWithRealTimeQuantity(selectedProduct)}
        />
      )}

    </Container>
  );
};
